/**
 * Long-lived WebSocket client for the agent dashboard at /v1/agent-ws.
 *
 * Connects with a single-use ticket fetched from the API, falls back to
 * exponential-backoff reconnect on transient failures, and forwards both
 * messages and connection-state changes to the registered listener.
 */
import type { AgentClientMsg, AgentServerMsg } from '@bruno/shared';
import { API_URL } from '../config';
import { getWsTicket } from '../api/team';

export type AgentSocketState = 'connecting' | 'open' | 'closed' | 'error';

export interface AgentSocketOpts {
  onMessage: (msg: AgentServerMsg) => void;
  onState: (state: AgentSocketState) => void;
}

const MAX_RECONNECT_ATTEMPTS = 30;

export class AgentSocket {
  private socket: WebSocket | null = null;
  private heartbeatTimer: number | null = null;
  private reconnectAttempts = 0;
  private closedByUser = false;

  constructor(private opts: AgentSocketOpts) {}

  async connect(): Promise<void> {
    if (
      this.socket?.readyState === WebSocket.OPEN ||
      this.socket?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }
    this.closedByUser = false;
    this.opts.onState('connecting');
    let ticket: string;
    try {
      const res = await getWsTicket();
      ticket = res.ticket;
    } catch {
      this.opts.onState('error');
      this.scheduleReconnect();
      return;
    }
    const wsBase = API_URL.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/v1/agent-ws?ticket=${encodeURIComponent(ticket)}`);
    this.socket = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.opts.onState('open');
      this.startHeartbeat();
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as AgentServerMsg;
        this.opts.onMessage(msg);
      } catch {
        /* ignore malformed */
      }
    };
    ws.onclose = () => {
      this.stopHeartbeat();
      if (this.closedByUser) {
        this.opts.onState('closed');
        return;
      }
      this.opts.onState('closed');
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      this.opts.onState('error');
    };
  }

  send(msg: AgentClientMsg): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(msg));
    return true;
  }

  close(): void {
    this.closedByUser = true;
    this.stopHeartbeat();
    try {
      this.socket?.close(1000, 'user');
    } catch {
      /* ignore */
    }
    this.socket = null;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      this.send({ type: 'heartbeat' });
    }, 25_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts += 1;
    if (this.reconnectAttempts > MAX_RECONNECT_ATTEMPTS) return;
    const delay = Math.min(30_000, 1000 * 2 ** Math.min(this.reconnectAttempts, 5));
    window.setTimeout(() => {
      if (!this.closedByUser) void this.connect();
    }, delay);
  }
}
